import { useNavigate, useParams } from 'react-router';
import { fmtDateWeek, fmtMonthDay, LEAVE_LABEL } from '@wd/shared';
import { TopBar } from '../../app/AppShell';
import { errorText } from '../../lib/api';
import { todayCN } from '../../lib/clock';
import { useLayout } from '../../lib/media';
import { useLeaveActions, useReport } from '../../lib/queries';
import { Button, EmptyState, Skeleton } from '../../ui/primitives';
import { ReportDesktop } from './ReportDesktop';
import { ReportMobile } from './ReportMobile';
import { useReportEditor } from './useReportEditor';
import './report.css';

export function ReportPage() {
  const { date: param } = useParams();
  const today = todayCN();
  const date = param ?? today;
  const layout = useLayout();
  const navigate = useNavigate();
  const { data: report, error, isLoading } = useReport(date === today ? 'today' : date);
  const ed = useReportEditor(report);
  const leaveActions = useLeaveActions();
  const mobile = layout === 'mobile';

  if (isLoading) return <ReportSkeleton mobile={mobile} />;
  if (error || !report) {
    return (
      <>
        {!mobile && <TopBar crumbs={['我的日报', fmtMonthDay(date)]} icon="doc" />}
        <EmptyState icon="doc" title={date === today ? '日报没有加载出来' : `${fmtDateWeek(date)} 没有日报`} desc={error ? errorText(error) : undefined}>
          <Button onClick={() => navigate('/')}>回到今天</Button>
        </EmptyState>
      </>
    );
  }

  if (report.status === 'on_leave' && report.leave) {
    const l = report.leave;
    return (
      <>
        {!mobile && <TopBar crumbs={['我的日报', fmtMonthDay(date)]} icon="doc" />}
        <EmptyState
          icon={l.type === 'trip' ? 'plane' : 'sun'}
          title={`${fmtMonthDay(l.from)}${l.to !== l.from ? ` – ${fmtMonthDay(l.to)}` : ''} ${LEAVE_LABEL[l.type]}中`}
          desc="这段时间不提醒、不预填，你负责的项目暂停计算停滞天数。"
        >
          <Button mobile={mobile} onClick={() => leaveActions.cancel.mutate(l.id)}>
            撤销，今天照常写
          </Button>
        </EmptyState>
      </>
    );
  }

  return mobile ? <ReportMobile report={report} ed={ed} today={today} /> : <ReportDesktop report={report} ed={ed} layout={layout} today={today} />;
}

function ReportSkeleton({ mobile }: { mobile: boolean }) {
  const pad = mobile ? '24px 20px' : '44px 56px 20px 64px';
  return (
    <div style={{ padding: pad, display: 'flex', flexDirection: 'column', gap: 14 }} aria-busy="true" aria-label="加载中">
      <Skeleton w={160} h={22} />
      <Skeleton w={260} h={12} />
      {!mobile && <Skeleton h={120} style={{ marginTop: 24 }} />}
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} h={mobile ? 40 : 24} style={{ marginTop: 8 }} />
      ))}
    </div>
  );
}
